import React, { useState, useEffect } from 'react';
import { get } from 'lodash';
import moment from 'moment';
import {
  Button,
  BaseCard,
  BaseCardHeader,
  BaseCardBody,
  Filter,
  FilterSingle,
  Icon,
  Table,
  TableHeader,
  TableRow,
  TableHeaderColumn,
  TableSortableHeaderColumn,
  TableBody,
  TableRowColumn,
  CheckBoxField,
} from '@unite-us/app-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
  UPDATE_TASKSV3_FILTERS,
  RESET_TASKSV3_FILTERS,
} from 'actions';
import {
  wrk31LeadScnProviders,
  wrk102TasksInternalUser,
} from 'src/common/utils/FeatureFlags/flags';
import { hasUserRole } from 'src/components/User/utils';
import { useFind } from 'src/api/APIHooks';
import { browserHistory } from 'common/utils/browserHistory';
import Pagination from 'src/common/Pagination/Pagination';
import NewTaskModal from './NewTaskModal';
import ReassignTasksModal from './ReassignTasksModal';
import SyncScreeningTasksModal from './SyncScreeningTasksModal';
import BulkStatusUpdateModal from './BulkStatusUpdateModal';
import ScreeningFiltersDrawer from './ScreeningFiltersDrawer';
import TaskDetailsDrawer from './TaskDetailsDrawer';
import StatusBadge from './StatusBadge';
import TasksV3Sidebar from './TasksV3Sidebar';
import {
  useTasks,
  useBatchUpdateTaskStatus,
  useTasksCron,
  useTaskSendOrganizations,
  TASKS_PAGE_SIZE,
} from './tasksQueries';
import { notifyTaskStatusUpdatedSuccess, notifyTaskStatusUpdatedError } from './taskNotifications';
import {
  DUE_DATE_LABELS,
  DUE_DATE_RANGE_MAP,
  DATE_FILTER_OPTIONS,
  PAGE_TITLES,
  ROUTE_SEGMENTS,
  TASK_STATUS_OPTIONS,
} from './constants';
import '../tasks/Tasks.scss';
import './TasksV2.scss';

const DEFAULT_STATUS_FILTER = ['to_do', 'overdue'];
const getDefaultProviderFilter = (currentProviderId, isScnLeadProvider) => (
  isScnLeadProvider && currentProviderId ? [currentProviderId] : []
);
const getTasksRouteKey = (pathname = '') => {
  if (pathname.includes(ROUTE_SEGMENTS.INBOUND_SCREENINGS)) return ROUTE_SEGMENTS.INBOUND_SCREENINGS;
  if (pathname.includes(ROUTE_SEGMENTS.USER_CREATED)) return ROUTE_SEGMENTS.USER_CREATED;
  return ROUTE_SEGMENTS.ALL;
};

export const TasksV3 = (props) => {
  const {
    canSeePage,
    router,
    location,
    currentProviderId,
    currentEmployeeId,
    leadSCNFlag,
    hasWrk102TasksInternalUser,
    statusFilter,
    dateFilter,
    providerFilter,
    assigneeFilter,
    submittingOrgFilter,
    countyFilter,
    languageFilter,
    identifiedNeedsFilter,
    identifiedNeedsCountFilter,
    enhancedPopulationsFilter,
    updateRouteFilters,
    resetRouteFilters,
    children,
  } = props;

  const [selectedTasks, setSelectedTasks] = useState([]);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [pendingStatusAction, setPendingStatusAction] = useState('complete');
  const [processingJobType, setProcessingJobType] = useState(null);
  const [taskSort, setTaskSort] = useState({ sortBy: 'due_date', sortDirection: 'desc' });
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const [drawerTaskId, setDrawerTaskId] = useState(null);
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const DATE_OPTIONS = DATE_FILTER_OPTIONS.map((label) => ({
    label,
    value: label,
    initial: label === dateFilter,
  }));

  const statusOptions = TASK_STATUS_OPTIONS.map((s) => ({
    ...s,
    initial: statusFilter.includes(s.value),
  }));
  const bulkActionButtonClassName = [
    'text-sm text-action-blue font-semibold border',
    'border-solid border-medium-border-blue px-3 py-1.5 rounded',
    'disabled:opacity-40 disabled:cursor-not-allowed',
  ].join(' ');

  const { data: employeesResponse, isLoading: loadingAssignees } = useFind(
    'employee',
    { provider: currentProviderId },
    { queryConfig: { enabled: !!currentProviderId, placeholderData: undefined } },
  );
  const employees = get(employeesResponse, 'data.data', []);
  const employeeAssigneeOptions = employees
    .filter((employee) => employee?.id && employee?.full_name)
    .map((employee) => ({
      value: employee.id,
      label: employee.full_name,
      initial: assigneeFilter.includes(employee.id),
    }));
  const { pathname } = location;
  const currentRouteKey = getTasksRouteKey(pathname);
  const setRouteFilters = (filters) => updateRouteFilters(currentRouteKey, filters);

  let subjectTypeFilter;
  if (pathname.includes(ROUTE_SEGMENTS.INBOUND_SCREENINGS)) {
    subjectTypeFilter = 'Screening';
  } else if (pathname.includes(ROUTE_SEGMENTS.USER_CREATED)) {
    subjectTypeFilter = 'Employee';
  }
  const isInboundScreeningsTab = pathname.includes(ROUTE_SEGMENTS.INBOUND_SCREENINGS);

  const taskSendOrganizationsData = useTaskSendOrganizations(currentProviderId, leadSCNFlag);
  const providerOptions = taskSendOrganizationsData.orgOptions.map((orgOption) => ({
    value: orgOption.value,
    label: orgOption.label,
    initial: providerFilter.includes(orgOption.value),
  }));
  const selectedProviderIds = providerFilter.length ? providerFilter.join(',') : currentProviderId;
  const sortValue = taskSort.sortBy ?
    `${taskSort.sortDirection === 'desc' ? '-' : ''}${taskSort.sortBy}` :
    undefined;

  const { data: tasksData, isLoading: loadingTasks } = useTasks({
    provider: selectedProviderIds,
    assignee: assigneeFilter.length ? assigneeFilter.join(',') : undefined,
    status: statusFilter.length ? statusFilter.join(',') : undefined,
    county: countyFilter,
    language: languageFilter,
    subjectType: subjectTypeFilter,
    identifiedNeeds: identifiedNeedsFilter,
    identifiedNeedsCount: identifiedNeedsCountFilter,
    enhancedPopulations: enhancedPopulationsFilter,
    submitterIds: submittingOrgFilter,
    dueDateRange: DUE_DATE_RANGE_MAP[dateFilter],
    sort: sortValue,
    page: currentPage,
  });
  const tasks = tasksData?.data ?? [];
  const tasksPaging = tasksData?.paging;
  const totalTaskCount = tasksPaging?.total_count;
  const totalTaskPages = tasksPaging?.total_pages;

  const { mutateAsync: batchUpdateTaskStatus, isLoading: isUpdatingStatus } = useBatchUpdateTaskStatus();
  const { mutateAsync: runTasksCron, isLoading: isSyncing } = useTasksCron();
  const handleTasksCron = async (jobType) => {
    setProcessingJobType(jobType);
    try {
      await runTasksCron({ jobType });
      setShowSyncModal(false);
    } finally {
      setProcessingJobType(null);
    }
  };

  const selectedTaskObjects = tasks.filter((t) => selectedTasks.includes(t.id));

  const openStatusUpdateModal = (statusAction) => {
    setPendingStatusAction(statusAction);
    setShowStatusUpdateModal(true);
  };

  const handleStatusUpdateConfirm = async () => {
    const selectedTaskCount = selectedTasks.length;
    const taskIds = [...selectedTasks].join(',');
    if (!taskIds.length) return;

    try {
      await batchUpdateTaskStatus({
        taskIds,
        status: pendingStatusAction,
      });
      notifyTaskStatusUpdatedSuccess(pendingStatusAction, selectedTaskCount);
      setSelectedTasks([]);
      setShowStatusUpdateModal(false);
    } catch (error) {
      notifyTaskStatusUpdatedError();
    }
  };

  if (!canSeePage) {
    router.push('/');
  }

  useEffect(() => {
    if (pathname === '/tasksv3') {
      router.replace('/tasksv3/all');
    }
  }, [pathname, router]);

  useEffect(() => {
    if (!taskSendOrganizationsData.isScnLeadProvider || !currentProviderId) {
      if (providerFilter.length > 0) {
        updateRouteFilters(currentRouteKey, { providerFilter: [] });
      }
      return;
    }

    if (!providerFilter.length) {
      updateRouteFilters(currentRouteKey, {
        providerFilter: getDefaultProviderFilter(currentProviderId, taskSendOrganizationsData.isScnLeadProvider),
      });
    }
  }, [
    currentProviderId,
    currentRouteKey,
    providerFilter,
    updateRouteFilters,
    taskSendOrganizationsData.isScnLeadProvider,
  ]);

  useEffect(() => {
    setSelectedTasks([]);
  }, [
    currentPage,
    statusFilter,
    dateFilter,
    providerFilter,
    assigneeFilter,
    submittingOrgFilter,
    countyFilter,
    languageFilter,
    identifiedNeedsFilter,
    identifiedNeedsCountFilter,
    enhancedPopulationsFilter,
    subjectTypeFilter,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    statusFilter,
    dateFilter,
    providerFilter,
    assigneeFilter,
    submittingOrgFilter,
    countyFilter,
    languageFilter,
    identifiedNeedsFilter,
    identifiedNeedsCountFilter,
    enhancedPopulationsFilter,
    subjectTypeFilter,
    taskSort.sortBy,
    taskSort.sortDirection,
  ]);

  const getPageTitle = () => {
    if (pathname.includes(ROUTE_SEGMENTS.INBOUND_SCREENINGS)) return PAGE_TITLES.INBOUND_SCREENINGS;
    if (pathname.includes(ROUTE_SEGMENTS.USER_CREATED)) return PAGE_TITLES.USER_CREATED;
    return PAGE_TITLES.ALL_TASKS;
  };

  const handleAfterReassignSubmit = () => {
    setSelectedTasks([]);
  };

  const handleAfterTaskCreate = () => {
    setSelectedTasks([]);
    if (pathname.includes(ROUTE_SEGMENTS.INBOUND_SCREENINGS)) {
      router.push('/tasksv3/all');
    }
  };

  const drawerFilterCount = submittingOrgFilter.length +
    countyFilter.length +
    languageFilter.length +
    identifiedNeedsFilter.length +
    identifiedNeedsCountFilter.length +
    enhancedPopulationsFilter.length;
  const getPreferredLanguages = (task) => (
    task?.person?.record_languages?.length ?
      task.person.record_languages
        .map((recordLanguage) => recordLanguage?.language_name)
        .filter(Boolean)
        .join(', ') :
      '—'
  );
  const getEnhancedPopulationLabels = (task) => {
    const labels = (task?.person?.initiative_conditions ?? [])
      .map((condition) => condition?.initiative_description)
      .filter(Boolean);

    return labels.length ? [...new Set(labels)].join(', ') : '—';
  };
  const getDueDateLabel = (task) => {
    if (task.due_date || task.due_at) {
      return moment(task.due_date ?? task.due_at).format('MMMM D, YYYY');
    }

    return '—';
  };
  const getThirdColumnValue = (task) => {
    if (isInboundScreeningsTab) {
      return task?.task_reference?.screening_identified_needs_count ?? '—';
    }

    return task?.assignee_employee?.full_name ?? '—';
  };
  const getFourthColumnValue = (task) => {
    if (isInboundScreeningsTab) {
      return getPreferredLanguages(task);
    }

    return task?.assignee_provider?.name ?? '—';
  };
  const getFifthColumnValue = (task) => {
    if (isInboundScreeningsTab) {
      return task.person?.addresses?.[0]?.county ?? '—';
    }

    return getDueDateLabel(task);
  };
  const getFirstColumnValue = (task) => {
    if (isInboundScreeningsTab) {
      return task.person?.full_name ?? task.person?.name ?? '—';
    }

    return task.name ?? task.title ?? '—';
  };
  const getSecondColumnValue = (task) => {
    if (isInboundScreeningsTab) {
      return task?.submitter_provider?.name ?? 'External';
    }

    return task.person?.full_name ?? task.person?.name ?? '—';
  };

  const handleResetDrawerFilters = () => {
    setRouteFilters({
      submittingOrgFilter: [],
      countyFilter: [],
      languageFilter: [],
      identifiedNeedsFilter: [],
      identifiedNeedsCountFilter: [],
      enhancedPopulationsFilter: [],
    });
  };

  const handleClearFilters = () => {
    resetRouteFilters(currentRouteKey);
    setRouteFilters({
      statusFilter: [],
      dateFilter: DUE_DATE_LABELS.ALL_DATES,
      providerFilter: getDefaultProviderFilter(currentProviderId, taskSendOrganizationsData.isScnLeadProvider),
      assigneeFilter: [],
      submittingOrgFilter: [],
      countyFilter: [],
      languageFilter: [],
      identifiedNeedsFilter: [],
      identifiedNeedsCountFilter: [],
      enhancedPopulationsFilter: [],
    });
    setCurrentPage(1);
    setSelectedTasks([]);
  };

  const handleProviderFilterChange = (filters, _filterKey, callbacks = {}) => {
    const nextFilters = filters.length > 0 ?
      filters :
      getDefaultProviderFilter(currentProviderId, taskSendOrganizationsData.isScnLeadProvider);

    if (callbacks.setSelectedOptions) {
      callbacks.setSelectedOptions(nextFilters);
    }

    setRouteFilters({ providerFilter: nextFilters });
  };

  const handleOpenTaskDrawer = (event, task) => {
    event.stopPropagation();
    if (!task?.id) return;

    setDrawerTaskId(task.id);
    setIsTaskDrawerOpen(true);
  };

  const handleCloseTaskDrawer = () => {
    setIsTaskDrawerOpen(false);
    setDrawerTaskId(null);
  };

  const handleOpenTaskFullView = (task) => {
    if (!task?.id) return;

    handleCloseTaskDrawer();
    browserHistory.push(`/tasksv3/${task.id}`);
  };

  const handleOpenTaskView = (taskId) => {
    if (!taskId) return;
    browserHistory.push(`/tasksv3/${taskId}`);
  };

  const nav = <TasksV3Sidebar />;

  if (children) return children;

  const scrollPaginationElement = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  let resultsContent = (
    <div className="px-48 pb-48 pt-24 flex flex-col items-center text-center space-y-4">
      <h3 className="font-semibold">No Results Found</h3>
      <h4 className="my-6">
        No tasks match your current filter criteria. Try adjusting your filters to see more
        results.
      </h4>
      <Button
        secondary
        label="Clear All Filters"
        icon={{ name: 'CrossCircleO', color: 'action-blue', position: 'left' }}
        className="text-action-blue px-6"
        onClick={handleClearFilters}
      />
    </div>
  );

  if (loadingTasks) {
    resultsContent = (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        Loading tasks...
      </div>
    );
  } else if (tasks.length > 0) {
    resultsContent = (
      <div>
        <div className="tasks-v3-table-scroll">
          <Table id="tasks-v3-table" noBorder>
            <TableHeader className="bg-white">
              <TableRow>
                <TableHeaderColumn>
                  <CheckBoxField
                    id="tasks-v3-select-all"
                    label="Select all tasks"
                    hideLabel
                    checked={tasks.length > 0 && tasks.every((t) => selectedTasks.includes(t.id))}
                    onChange={() => {
                      const allSelected = tasks.length > 0 &&
                        tasks.every((t) => selectedTasks.includes(t.id));
                      setSelectedTasks(allSelected ? [] : tasks.map((t) => t.id));
                    }}
                    style={{ container: 'pb-0.5' }}
                  />
                </TableHeaderColumn>
                {isInboundScreeningsTab ? (
                  <>
                    <TableHeaderColumn />
                    <TableSortableHeaderColumn
                      colName="person.last_name"
                      label="client name"
                      sortBy={taskSort.sortBy}
                      sortDirection={taskSort.sortDirection}
                      setSort={setTaskSort}
                    >
                      Client Name
                    </TableSortableHeaderColumn>
                    <TableHeaderColumn>Submitting Org</TableHeaderColumn>
                    <TableSortableHeaderColumn
                      colName="screening_identified_needs_count"
                      label="needs"
                      sortBy={taskSort.sortBy}
                      sortDirection={taskSort.sortDirection}
                      setSort={setTaskSort}
                    >
                      Needs
                    </TableSortableHeaderColumn>
                    <TableHeaderColumn>Preferred Language</TableHeaderColumn>
                    <TableHeaderColumn>Client County</TableHeaderColumn>
                    <TableHeaderColumn>Enhanced Populations</TableHeaderColumn>
                  </>
                ) : (
                  <>
                    <TableHeaderColumn>Task Name</TableHeaderColumn>
                    <TableSortableHeaderColumn
                      colName="person.last_name"
                      label="client name"
                      sortBy={taskSort.sortBy}
                      sortDirection={taskSort.sortDirection}
                      setSort={setTaskSort}
                    >
                      Client Name
                    </TableSortableHeaderColumn>
                    <TableHeaderColumn>Assignee</TableHeaderColumn>
                    <TableHeaderColumn>Assigned Provider</TableHeaderColumn>
                    <TableSortableHeaderColumn
                      colName="due_date"
                      label="due date"
                      sortBy={taskSort.sortBy}
                      sortDirection={taskSort.sortDirection}
                      setSort={setTaskSort}
                    >
                      Due Date
                    </TableSortableHeaderColumn>
                  </>
                )}
                <TableSortableHeaderColumn
                  colName="status"
                  label="status"
                  sortBy={taskSort.sortBy}
                  sortDirection={taskSort.sortDirection}
                  setSort={setTaskSort}
                >
                  Status
                </TableSortableHeaderColumn>
              </TableRow>
            </TableHeader>
            <TableBody
              className="bg-white"
              onCellClick={(rowIndex, columnIndex) => {
                if (columnIndex === 0) return;
                handleOpenTaskView(tasks[rowIndex]?.id);
              }}
            >
              {tasks.map((task) => {
                const checked = selectedTasks.includes(task.id);
                return (
                  <TableRow key={task.id}>
                    <TableRowColumn>
                      <CheckBoxField
                        id={`tasks-v3-checkbox-${task.id}`}
                        label={`Select task ${task.name ?? task.id}`}
                        hideLabel
                        checked={checked}
                        onChange={() => {
                          setSelectedTasks((prev) => (
                            checked ? prev.filter((id) => id !== task.id) : [...prev, task.id]
                          ));
                        }}
                        style={{ container: 'pb-0.5' }}
                      />
                    </TableRowColumn>
                    {isInboundScreeningsTab && (
                      <TableRowColumn>
                        <button
                          type="button"
                          aria-label={`Open details drawer for ${task.name ?? task.id}`}
                          className="p-2 text-action-blue hover:bg-gray-100 rounded transition-colors"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenTaskDrawer(event, task);
                          }}
                        >
                          <Icon
                            ariaLabel={`Open details drawer for ${task.name ?? task.id}`}
                            icon="OneFourth"
                            color="action-blue"
                            direction="down"
                          />
                        </button>
                      </TableRowColumn>
                    )}
                    <TableRowColumn>
                      {getFirstColumnValue(task)}
                    </TableRowColumn>
                    <TableRowColumn>
                      {getSecondColumnValue(task)}
                    </TableRowColumn>
                    <TableRowColumn>{getThirdColumnValue(task)}</TableRowColumn>
                    <TableRowColumn>{getFourthColumnValue(task)}</TableRowColumn>
                    <TableRowColumn>{getFifthColumnValue(task)}</TableRowColumn>
                    {isInboundScreeningsTab && (
                      <TableRowColumn>
                        <span
                          className="block max-w-xs truncate"
                          title={getEnhancedPopulationLabels(task)}
                        >
                          {getEnhancedPopulationLabels(task)}
                        </span>
                      </TableRowColumn>
                    )}
                    <TableRowColumn><StatusBadge task={task} /></TableRowColumn>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <div className="tasks-v3-pagination-footer">
          <Pagination
            pageNumber={currentPage}
            pageSize={TASKS_PAGE_SIZE}
            setPageNumber={setCurrentPage}
            setPageSize={() => {}}
            totalItemCount={totalTaskCount}
            totalPageCount={totalTaskPages}
            scrollPaginationElement={scrollPaginationElement}
            showPageSizeSelector={false}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex antialiased min-h-full -mx-container-padding">
      {nav}

      <div className="flex-1 bg-blue-50 overflow-x-hidden">
        <div className="px-8 py-6">
          <BaseCard className="my-0 mx-auto w-12/12">
            <BaseCardHeader title={getPageTitle()} className="p-4 py-3 text-xl">
              <div className="flex items-center gap-2">
                {hasWrk102TasksInternalUser && (
                  <Button
                    data-testid="sync-screenings-button"
                    className="text-sm text-action-blue font-semibold border border-solid border-medium-border-blue"
                    onClick={() => setShowSyncModal(true)}
                    label="Internal Use"
                    icon={{
                      name: 'Warning',
                      color: 'action-blue',
                      position: 'left',
                    }}
                    omitDefaultStyles
                  />
                )}
                <Button
                  data-testid="create-task-button"
                  className="text-sm text-action-blue font-semibold border border-solid border-medium-border-blue"
                  onClick={() => {
                    setShowNewTaskModal(true);
                  }}
                  label="New Task"
                  icon={{
                    name: 'Plus',
                    color: 'action-blue',
                    position: 'left',
                  }}
                  omitDefaultStyles
                />
              </div>
            </BaseCardHeader>

            <BaseCardBody>
              {/* Filters and Actions Bar */}
              <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-4 border-b mb-4">
                {/* Bulk Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="reassign-button"
                    label="Reassign"
                    omitDefaultStyles
                    className={bulkActionButtonClassName}
                    disabled={selectedTasks.length === 0}
                    onClick={() => setShowReassignModal(true)}
                  />
                  <Button
                    data-testid="archive-button"
                    label="Archive"
                    omitDefaultStyles
                    className={bulkActionButtonClassName}
                    disabled={selectedTasks.length === 0 || isUpdatingStatus}
                    onClick={() => openStatusUpdateModal('archived')}
                  />
                  <Button
                    data-testid="mark-as-complete-button"
                    label="Mark As Complete"
                    omitDefaultStyles
                    className={bulkActionButtonClassName}
                    disabled={selectedTasks.length === 0 || isUpdatingStatus}
                    onClick={() => openStatusUpdateModal('complete')}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <Filter
                    id="filter-status"
                    name="Status"
                    pluralName="Statuses"
                    filterKey="status"
                    options={statusOptions}
                    onFiltersChange={(filters) => setRouteFilters({ statusFilter: filters })}
                    isActive={statusFilter.length > 0}
                  />
                  {taskSendOrganizationsData.isScnLeadProvider && (
                    <Filter
                      id="filter-providers"
                      name="Assignee Providers"
                      pluralName="Assignee Providers"
                      filterKey="provider"
                      options={providerOptions}
                      onFiltersChange={handleProviderFilterChange}
                      isActive={providerFilter.length > 0}
                    />
                  )}
                  <Filter
                    id="filter-assignees"
                    name={loadingAssignees ? 'Fetching Assignees...' : 'Assignees'}
                    pluralName="Assignees"
                    filterKey="assigned_to"
                    options={employeeAssigneeOptions}
                    onFiltersChange={(filters) => setRouteFilters({ assigneeFilter: filters })}
                    isActive={assigneeFilter.length > 0}
                  />
                  <div className="tasks-v3-date-filter">
                    <FilterSingle
                      id="filter-date"
                      name="Due Date"
                      filterKey="due_at"
                      options={DATE_OPTIONS}
                      onFilterChange={(filter) => setRouteFilters({
                        dateFilter: filter.value || DUE_DATE_LABELS.THIS_WEEK,
                      })}
                      value={dateFilter}
                      mustHaveOneSelected
                    />
                  </div>
                  {pathname.includes('/inbound-screenings') && (
                    <button
                      type="button"
                      aria-label="All Filters"
                      className="h-6 my-auto flex items-center"
                      onClick={() => setShowFiltersDrawer(true)}
                    >
                      <Icon
                        ariaLabel="All Filters"
                        className="fill-current text-brand-blue"
                        icon="Filter"
                        color="action-blue"
                      />
                      <span className="ml-1 text-action-blue text-sm font-semibold">
                        {`All Filters${drawerFilterCount > 0 ? ` (${drawerFilterCount})` : ''}`}
                      </span>
                    </button>
                  )}
                </div>
              </div>

              {/* Results Area */}
              {resultsContent}
            </BaseCardBody>
          </BaseCard>
        </div>
      </div>

      <NewTaskModal
        show={showNewTaskModal}
        onClose={() => setShowNewTaskModal(false)}
        onSuccess={handleAfterTaskCreate}
        currentProviderId={currentProviderId}
        currentEmployeeId={currentEmployeeId}
      />

      <ReassignTasksModal
        show={showReassignModal}
        onClose={() => setShowReassignModal(false)}
        onAfterSubmit={handleAfterReassignSubmit}
        selectedTasks={selectedTaskObjects}
        currentProviderId={currentProviderId}
      />

      <SyncScreeningTasksModal
        show={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        onProcessScreenings={() => handleTasksCron('screenings_task')}
        onProcessOverdueTasks={() => handleTasksCron('task_overdue')}
        processingJobType={isSyncing ? processingJobType : null}
        currentProviderId={currentProviderId}
        leadSCNFlag={leadSCNFlag}
      />

      <BulkStatusUpdateModal
        show={showStatusUpdateModal}
        onClose={() => setShowStatusUpdateModal(false)}
        onConfirm={handleStatusUpdateConfirm}
        loading={isUpdatingStatus}
        statusAction={pendingStatusAction}
        selectedCount={selectedTasks.length}
      />

      {showFiltersDrawer && (
        <ScreeningFiltersDrawer
          isOpen={showFiltersDrawer}
          onClose={() => setShowFiltersDrawer(false)}
          onReset={handleResetDrawerFilters}
          submittingOrgFilter={submittingOrgFilter}
          setSubmittingOrgFilter={(filters) => setRouteFilters({ submittingOrgFilter: filters })}
          countyFilter={countyFilter}
          setCountyFilter={(filters) => setRouteFilters({ countyFilter: filters })}
          countyOptions={[]}
          languageFilter={languageFilter}
          setLanguageFilter={(filters) => setRouteFilters({ languageFilter: filters })}
          identifiedNeedsFilter={identifiedNeedsFilter}
          setIdentifiedNeedsFilter={(filters) => setRouteFilters({ identifiedNeedsFilter: filters })}
          identifiedNeedsCountFilter={identifiedNeedsCountFilter}
          setIdentifiedNeedsCountFilter={(filters) => setRouteFilters({ identifiedNeedsCountFilter: filters })}
          enhancedPopulationsFilter={enhancedPopulationsFilter}
          setEnhancedPopulationsFilter={(filters) => setRouteFilters({ enhancedPopulationsFilter: filters })}
        />
      )}

      <TaskDetailsDrawer
        taskId={drawerTaskId}
        isOpen={isTaskDrawerOpen}
        onClose={handleCloseTaskDrawer}
        onOpenFullView={handleOpenTaskFullView}
      />
    </div>
  );
};

TasksV3.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  canSeePage: PropTypes.bool.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    isActive: PropTypes.func.isRequired,
  }).isRequired,
  currentProviderId: PropTypes.string.isRequired,
  currentEmployeeId: PropTypes.string,
  leadSCNFlag: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  hasWrk102TasksInternalUser: PropTypes.bool.isRequired,
  statusFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  dateFilter: PropTypes.string.isRequired,
  providerFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  assigneeFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  submittingOrgFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  countyFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  languageFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  identifiedNeedsFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  identifiedNeedsCountFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  enhancedPopulationsFilter: PropTypes.arrayOf(PropTypes.string).isRequired,
  updateRouteFilters: PropTypes.func.isRequired,
  resetRouteFilters: PropTypes.func.isRequired,
  children: PropTypes.node,
};

TasksV3.defaultProps = {
  currentEmployeeId: null,
  leadSCNFlag: null,
  children: null,
};

function mapStateToProps(state, ownProps) {
  const { globalState } = state;
  const pathname = get(ownProps, 'location.pathname', '');
  const currentRouteKey = getTasksRouteKey(pathname);
  const currentEmployeeId = get(globalState, 'currentEmployee.id', null);
  const currentProviderId = get(globalState, 'currentProvider.group.id', null) || state.session.groupId;
  const leadSCNFlag = wrk31LeadScnProviders(state);
  const hasWrk102TasksInternalUser = wrk102TasksInternalUser(state);
  const routeFilters = get(state, `tasksV3.filtersByRoute.${currentRouteKey}`, {});
  const canSeePage = hasUserRole({
    groupId: state.session.groupId,
    name: 'Tasks',
    roleType: 'feature',
    user: state.user,
  });

  return {
    currentEmployeeId,
    currentProviderId,
    canSeePage,
    leadSCNFlag,
    hasWrk102TasksInternalUser,
    statusFilter: routeFilters.statusFilter ?? DEFAULT_STATUS_FILTER,
    dateFilter: routeFilters.dateFilter ?? DUE_DATE_LABELS.ALL_DATES,
    providerFilter: routeFilters.providerFilter ?? [],
    assigneeFilter: routeFilters.assigneeFilter ?? [],
    submittingOrgFilter: routeFilters.submittingOrgFilter ?? [],
    countyFilter: routeFilters.countyFilter ?? [],
    languageFilter: routeFilters.languageFilter ?? [],
    identifiedNeedsFilter: routeFilters.identifiedNeedsFilter ?? [],
    identifiedNeedsCountFilter: routeFilters.identifiedNeedsCountFilter ?? [],
    enhancedPopulationsFilter: routeFilters.enhancedPopulationsFilter ?? [],
  };
}

const mapDispatchToProps = (dispatch) => ({
  updateRouteFilters: (routeKey, filters) => dispatch({
    type: UPDATE_TASKSV3_FILTERS,
    payload: {
      routeKey,
      filters,
    },
  }),
  resetRouteFilters: (routeKey) => dispatch({
    type: RESET_TASKSV3_FILTERS,
    payload: { routeKey },
  }),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(TasksV3));
